import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i3 from '@engineering11/web-api-error';
import { e11Error } from '@engineering11/web-api-error';
import { shareReplay, of, combineLatest, filter as filter$1, distinctUntilChanged as distinctUntilChanged$1, switchMap as switchMap$1, tap, map as map$1 } from 'rxjs';
import { filterNullish, hasAll, isNotNil, isDeepEqual, isTrue, isNil } from '@engineering11/utility';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { CollectionRepository, FirestoreQueryBuilder } from '@engineering11/web-api-firebase';
import { BaseLocalStorageService } from '@engineering11/web-utilities';
import { RestApiClient } from '@engineering11/web-api-rest';

class BaseAccessConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseAccessConfigProvider, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseAccessConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: BaseAccessConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
/**
 * @deprecated use BaseAccessConfigProvider - less name clashing
 * TODO: On breaking change, delete
 */
class AccessConfigProvider extends BaseAccessConfigProvider {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessConfigProvider, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessConfigProvider, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessConfigProvider, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class AccessModule {
    static forRoot(config) {
        return {
            ngModule: AccessModule,
            providers: [
                {
                    provide: BaseAccessConfigProvider,
                    useClass: config.configProvider,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.9", ngImport: i0, type: AccessModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: [],
                }]
        }] });

const SDK_NAME = 'access';
const COLLECTIONS = Object.freeze({
    ACCESS_PERMISSION_MAPS: `${SDK_NAME}_permission_maps/`,
    ACCESS_TOKEN: `${SDK_NAME}_token/`,
    USE_LIMIT: `use_limit/`,
});
const PERMISSION_MAPS_MAIN_DOC = `${SDK_NAME}_permission_maps_main`;
/**
 * Used to store the mapping from roles to the list of permissions that can be granted to this role (regardless of product)
 */
const PERMISSIBLE_ROLE_PERMISSIONS_DOC = `${SDK_NAME}_permissible_role_permissions`;
const ACCESS_ERRORS = {
    PERMISSIONS_DOC_MISSING: `${SDK_NAME}/permissions-doc-missing`,
    USE_LIMIT_NOT_CONFIGURED: `${SDK_NAME}/use-limit-not-configured`,
};

// ! Do not export from package - this should be internal as IPermissionMaps is subject to change
function getPermissionsGivenRolesAndProducts(roles, products, permissionMaps) {
    const { productAndRolePermissions: productAndRolePermissionsMap } = permissionMaps;
    const permissions = products
        ? filterNullish(products.map((product) => roles.map((role) => productAndRolePermissionsMap[product]?.[role])).flat(2))
        : [];
    return new Set(permissions);
}
/**
 *
 * @returns the list of roles the user has all permissions for
 */
function rolesEncompassedByUser(userPermissions, userProducts, productPermissions) {
    const permissionsForUsersProducts = getPermissionsForUsersProducts(userProducts, productPermissions);
    return Object.entries(permissionsForUsersProducts)
        .filter(([role, permissionsForRole]) => hasAll(userPermissions, permissionsForRole))
        .map(([role]) => role);
}
/**
 * Used to collect list of all permissions users could potentially have based on their products
 */
function getPermissionsForUsersProducts(products, productPermissions) {
    return products.reduce((map, product) => {
        if (!productPermissions[product])
            return map;
        Object.entries(productPermissions[product]).forEach(([role, permissions]) => {
            map[role] = [...(map[role] || []), ...permissions];
        });
        return map;
    }, {});
}

/**
 * ! Do not export this repository - the structure of permissions storage is very likely to change over time - storing permissions in firestore is intended to be temporary
 */
class PermissionMapsRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = COLLECTIONS.ACCESS_PERMISSION_MAPS;
    }
    getMainDocument() {
        return this.getOrError(PERMISSION_MAPS_MAIN_DOC);
    }
    getPermissibleRolePermissionsDocument() {
        return this.getOrError(PERMISSIBLE_ROLE_PERMISSIONS_DOC);
    }
    listenMainDocument() {
        return this.getValueChangesOrError(PERMISSION_MAPS_MAIN_DOC).pipe(shareReplay(1));
    }
    listenPermissibleRolePermissionsDocument() {
        return this.getValueChangesOrError(PERMISSIBLE_ROLE_PERMISSIONS_DOC).pipe(shareReplay(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionMapsRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionMapsRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionMapsRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/**
 * ! In most cases, PermissionStore should be used instead
 */
class PermissionService {
    constructor(configProvider, permissionMapsRepository, logger) {
        this.configProvider = configProvider;
        this.permissionMapsRepository = permissionMapsRepository;
        this.logger = logger;
        this.userId$ = this.configProvider.config.userId$.pipe(filter(isNotNil), distinctUntilChanged());
        this.roles$ = this.configProvider.config.roles$.pipe(filter(isNotNil), distinctUntilChanged());
        this.products$ = this.configProvider.config.products$?.pipe(filter(isNotNil), distinctUntilChanged()) ?? of([]);
    }
    listenPermissionMaps() {
        // TODO: Consider whether I need to unsubscribe from collection when userId is nil
        return this.userId$.pipe(switchMap((_) => this.permissionMapsRepository.listenMainDocument())); // Prevent fetching before the user is logged on
    }
    listenPermissibleRolePermissions() {
        return this.userId$.pipe(switchMap((_) => this.permissionMapsRepository.listenPermissibleRolePermissionsDocument()));
    }
    getPermissibleRolePermissions() {
        return this.userId$.pipe(switchMap((_) => this.permissionMapsRepository.getPermissibleRolePermissionsDocument()));
    }
    listenPermissionsForUser() {
        const permissionMaps$ = this.listenPermissionMaps();
        return combineLatest([this.roles$, this.products$, permissionMaps$]).pipe(map(([roles, products, permissionMaps]) => getPermissionsGivenRolesAndProducts(roles, products, permissionMaps)), distinctUntilChanged(isDeepEqual) // Only refire if permissions have changed
        );
    }
    listenAllRoles() {
        return this.listenPermissibleRolePermissions().pipe(map((permissionMaps) => Object.keys(permissionMaps.permissibleRolePermissions)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionService, deps: [{ token: BaseAccessConfigProvider }, { token: PermissionMapsRepository }, { token: i3.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: BaseAccessConfigProvider }, { type: PermissionMapsRepository }, { type: i3.E11Logger }] });

class AccessLocalStorageService extends BaseLocalStorageService {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessLocalStorageService, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessLocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessLocalStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const initialState = {
    userPermissions: null,
    loaded: false,
    permissionMaps: null,
    permissibleRolePermissions: null,
};
class PermissionStore extends ComponentStore {
    constructor(configProvider, permissionService, errorHandler, logger, localStorageService) {
        super(initialState);
        this.configProvider = configProvider;
        this.permissionService = permissionService;
        this.errorHandler = errorHandler;
        this.logger = logger;
        this.localStorageService = localStorageService;
        this.roles$ = this.configProvider.config.roles$.pipe(filter$1(isNotNil), distinctUntilChanged$1());
        this.products$ = this.configProvider.config.products$?.pipe(filter$1(isNotNil), distinctUntilChanged$1());
        // SELECTORS
        this.getState = this.select((s) => s);
        this.loaded$ = this.select((s) => s.loaded);
        this.userPermissions$ = this.select((s) => s.userPermissions).pipe(distinctUntilChanged$1(isDeepEqual)); // Deep equality check necessary for sets
        this.hasPermission = (permission) => this.select(this.userPermissions$, (permissions) => permissions?.has(permission) ?? false);
        this.hasAnyPermission = (permissions) => this.select(this.userPermissions$, (userPermissions) => permissions.some((permission) => userPermissions?.has(permission) ?? false));
        this.hasAllPermissions = (permissions) => this.select(this.userPermissions$, (userPermissions) => permissions.every((permission) => userPermissions?.has(permission) ?? false));
        this.permissibleRolePermissions$ = this.select((s) => s.permissibleRolePermissions);
        this.permissionMaps$ = this.select((s) => s.permissionMaps); // ! Do not expose externally
        /** Roles whose permissions the user has */
        this.encompassedRoles$ = this.select(this.products$, this.userPermissions$, this.permissionMaps$, (userProducts, userPermissions, permissionMaps) => userPermissions && permissionMaps
            ? rolesEncompassedByUser(Array.from(userPermissions), userProducts, permissionMaps.productAndRolePermissions)
            : null);
        // EFFECTS
        this.onListenPermissionMaps = this.effect((_$) => _$.pipe(switchMap$1((_) => {
            return this.permissionService.listenPermissionMaps().pipe(tapResponse((permissionMaps) => {
                return this.onPermissionMapsChanged(permissionMaps);
            }, (error) => this.errorHandler.handleError(error)));
        })));
        this.onGetPermissibleRolePermissions = this.effect((_$) => _$.pipe(switchMap$1((_) => this.permissionService.getPermissibleRolePermissions().pipe(tapResponse((permissibleRolePermissions) => {
            return this.onPermissibleRolePermissionsChanged(permissibleRolePermissions);
        }, (error) => this.errorHandler.handleError(error))))));
        this.onListenPermissions = this.effect((_$) => combineLatest([this.permissionMaps$.pipe(filter$1(isNotNil)), this.roles$, this.products$]).pipe(tap(([permissionMaps, roles, products]) => {
            const permissions = getPermissionsGivenRolesAndProducts(roles, products, permissionMaps);
            this.localStorageService.setItem('userPermissions', Array.from(permissions));
            this.onPermissionsChanged(permissions);
        })));
        this.initPermissions = this.effect((_$) => _$.pipe(map$1((_) => {
            const userPermissions = this.localStorageService.getItem('userPermissions');
            if (userPermissions)
                this.onPermissionsChanged(new Set(userPermissions));
        })));
        // UPDATERS
        this.onPermissionsChanged = this.updater((state, permissions) => ({
            ...state,
            userPermissions: permissions,
            loaded: true,
        }));
        this.onPermissionMapsChanged = this.updater((state, permissionMaps) => ({
            ...state,
            permissionMaps,
        }));
        this.onPermissibleRolePermissionsChanged = this.updater((state, permissibleRolePermissions) => ({
            ...state,
            permissibleRolePermissions,
        }));
        this.initPermissions();
        this.onListenPermissionMaps();
        this.onListenPermissions();
        this.onGetPermissibleRolePermissions();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionStore, deps: [{ token: BaseAccessConfigProvider }, { token: PermissionService }, { token: i3.E11ErrorHandlerService }, { token: i3.E11Logger }, { token: AccessLocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionStore, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionStore, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: BaseAccessConfigProvider }, { type: PermissionService }, { type: i3.E11ErrorHandlerService }, { type: i3.E11Logger }, { type: AccessLocalStorageService }] });

/**
 * Expecting data field in route - data: { permission: Permission.PermissionName },
 */
class PermissionsGuard {
    constructor(permissionStore, logger) {
        this.permissionStore = permissionStore;
        this.logger = logger;
    }
    canActivate(route, state) {
        const permission = route.data.permission;
        if (!permission)
            throw e11Error(permissionNotInRouteCheckData);
        const permissions = Array.isArray(permission) ? permission : [permission];
        this.logger.debug('PermissionsGuard - checking if user has permissions: ', permissions);
        return this.permissionStore.loaded$.pipe(filter$1(isTrue), switchMap$1(() => this.permissionStore.hasAllPermissions(permissions)), tap((hasPermission) => {
            if (!hasPermission)
                this.logger.warn('PermissionsGuard - user does not have permission: ', permission);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionsGuard, deps: [{ token: PermissionStore }, { token: i3.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionsGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: PermissionsGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: PermissionStore }, { type: i3.E11Logger }] });
const permissionNotInRouteCheckData = {
    type: 'permission-not-in-route',
    title: 'Add - data: { permission: Permission.PermissionName } to the route with this guard!',
};

class AccessTokenRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = COLLECTIONS.ACCESS_TOKEN;
    }
    /**
     * type should be defined by the concrete repository extending this one
     * @param ownerId
     * @param type
     * @returns
     */
    getAllByOwnerAndType(ownerId, type) {
        const query = new FirestoreQueryBuilder().where('ownerId', '==', ownerId).where('type', '==', type);
        return this.query(query.build());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessTokenRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessTokenRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: AccessTokenRepository, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class TokenManagementService {
    constructor(options) {
        this.restApiClient = new RestApiClient(options);
    }
    disableToken(token) {
        const accessToken = typeof token === 'string' ? token : token.token;
        return this.restApiClient.put(`token/${accessToken}/disable`, {});
    }
    enableToken(token) {
        const accessToken = typeof token === 'string' ? token : token.token;
        return this.restApiClient.put(`token/${accessToken}/enable`, {});
    }
    deleteToken(token) {
        const accessToken = typeof token === 'string' ? token : token.token;
        return this.restApiClient.delete(`token/${accessToken}`);
    }
}

class UseLimitRepository extends CollectionRepository {
    constructor() {
        super(...arguments);
        this.COLLECTION = COLLECTIONS.USE_LIMIT;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitRepository, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitRepository, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitRepository, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class UseLimitService {
    constructor(useLimitRepository, configProvider, logger) {
        this.useLimitRepository = useLimitRepository;
        this.configProvider = configProvider;
        this.logger = logger;
        this.userId$ = this.configProvider.config.userId$.pipe(filter$1(isNotNil));
    }
    /**
     * TODO: Consider whether a listener is necessary
     * @param request
     * @returns
     */
    listenLimit(request) {
        this.logger.log('listenRemainingUses', request);
        return this.userId$.pipe(tap((userId) => this.logger.log({ userId })), switchMap$1((userId) => {
            const id = this.buildId({ ...request, userId });
            return this.useLimitRepository.getValueChanges(id);
        }), map$1((limit) => {
            if (limit)
                return { useLimit: limit.useLimit, remainingUses: limit.useLimit - limit.useCount };
            const limitDefaults = this.configProvider.config.useLimitDefaults;
            if (!limitDefaults || isNil(limitDefaults[request.type]))
                throw e11Error(useLimitDefaultConfigError(request.type));
            return { useLimit: limitDefaults[request.type], remainingUses: limitDefaults[request.type] };
        }));
    }
    buildId(request) {
        const baseId = `${request.userId}_${request.type}`;
        if (request.itemId)
            return `${baseId}_${request.itemId}`;
        return baseId;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitService, deps: [{ token: UseLimitRepository }, { token: BaseAccessConfigProvider }, { token: i3.E11Logger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: UseLimitService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: UseLimitRepository }, { type: BaseAccessConfigProvider }, { type: i3.E11Logger }] });
const useLimitDefaultConfigError = (type) => ({
    type: ACCESS_ERRORS.USE_LIMIT_NOT_CONFIGURED,
    title: 'Configuration error',
    message: `Need to configure a default use limit for type: ${type}`,
});

/**
 * Assists SDKs in displaying roles
 * Works best if `roleDisplayConfig` is passed into the access config provider
 */
class RoleDisplayService {
    constructor(permissionStore, configProvider) {
        this.permissionStore = permissionStore;
        this.configProvider = configProvider;
        this.roleDisplayConfig = this.configProvider.config.roleDisplayConfig;
        this.availableRoles$ = this.permissionStore.encompassedRoles$.pipe(map$1((encompassedRoles) => {
            const allRoles = this.roleDisplayConfig?.allRoles;
            if (!allRoles)
                return encompassedRoles;
            return encompassedRoles?.filter((role) => this.isListedRole(role));
        }));
        this.availableRoleSelectOptions$ = this.availableRoles$.pipe(map$1((availableRoles) => availableRoles?.map((role) => this.roleToSelectOption(role))));
    }
    roleToSelectOption(role) {
        if (!this.isListedRole(role))
            return { label: role, value: role };
        const label = this.roleDisplayConfig ? this.roleDisplayConfig.roleLabelMap[role] ?? role : role;
        const description = this.roleDisplayConfig ? this.roleDisplayConfig.roleLabelMap[role] : undefined;
        return { label, value: role, description };
    }
    isListedRole(role) {
        const allRoles = this.roleDisplayConfig?.allRoles;
        if (!allRoles)
            return false;
        return Object.values(allRoles).includes(role);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: RoleDisplayService, deps: [{ token: PermissionStore }, { token: BaseAccessConfigProvider }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: RoleDisplayService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.9", ngImport: i0, type: RoleDisplayService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: PermissionStore }, { type: BaseAccessConfigProvider }] });

/*
 * Public API Surface of access
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ACCESS_ERRORS, AccessConfigProvider, AccessModule, AccessTokenRepository, BaseAccessConfigProvider, COLLECTIONS, PERMISSIBLE_ROLE_PERMISSIONS_DOC, PERMISSION_MAPS_MAIN_DOC, PermissionStore, PermissionsGuard, RoleDisplayService, SDK_NAME, TokenManagementService, UseLimitService };
